import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, HostListener, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonService } from '../../services/common.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SendlaneDetailsModalComponent } from './sendlane-details-modal/sendlane-details-modal.component';
import { formatDate } from '@angular/common';
import { debounceTime, fromEvent, Subscription } from 'rxjs';

interface StatusOption {
  label: string;
  value: number | undefined;
}

@Component({
  selector: 'app-sendlane-postscript',
  templateUrl: './sendlane-postscript.component.html',
  styleUrls: ['./sendlane-postscript.component.scss']
})
export class SendlanePostscriptComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('timelineContainer') timelineContainer?: ElementRef;
  @ViewChild('mainContainer') mainContainer?: ElementRef;
  
  @Input() isFailed?: number;
  @Input() isCompleted: number = 0;
  @Input() fromDate?: string;
  @Input() toDate?: string;
  @Input() perPage: number = 10;
  @Input() showFilters: boolean = true;
  @Input() autoLoad: boolean = true;
  @Input() initialData?: any;
  @Input() optionName?: string;
  @Input() registrationId?: string;
  @Input() isCallFromOrderReviewDeatil?: boolean = false;
  @Input() parent?: any; // Reference to the parent LogsComponent
  @Input() set forceRefresh(value: boolean) {
    if (value) {
      this.refresh();
      // Reset the refresh flag in the parent component
      if (this.parent) {
        this.parent.resetCampaignRefresh();
      }
    }
  }

  @Output() dataLoaded = new EventEmitter<any>();
  @Output() error = new EventEmitter<any>();

  // Date range properties
  showFilterDropdown: boolean = false;
  showCustomDatePicker: boolean = false;
  selectedDateFilter: string = 'default'; // Changed from '7days' to 'default'
  customDateRange: Date[] = [];
  tempCustomDateRange: Date[] = [];
  today: Date = new Date();
  yearRange: string = '2000:2030';
  isDateFilterApplied: boolean = false;

  // Options for dropdowns
  statusOptions: StatusOption[] = [
    { label: 'All', value: undefined },
    { label: 'Failed', value: 1 },
    { label: 'Not Failed', value: 0 }
  ];

  completionOptions: StatusOption[] = [
    { label: 'All', value: 0 },
    { label: 'Completed', value: 1 },
    { label: 'Failed', value: 2 }
  ];

  public data: any[] = [];
  public totalRecords: number = 0;
  public currentPage: number = 1;
  public Math = Math;

  // Calendar configuration
  minDate: Date = new Date(2000, 0, 1);
  maxDate: Date = new Date();
  dateFormat: string = 'yy-mm-dd';

  // Store initial input values
  private initialIsFailed?: number;
  private initialIsCompleted?: number;
  private initialFromDate?: string;
  private initialToDate?: string;
  private retryFailedValue?: number;

  private scrollSubscription?: Subscription;
  public isLoadingMore: boolean = false;
  private hasMoreData: boolean = true;
  private currentOffset: number = 0;
  private totalPages: number = 0;

  private previousCompletionStatus: number = 0;

  constructor(
    private commonService: CommonService,
    private modalService: NgbModal,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Close dropdowns when clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-button-container')) {
      this.showFilterDropdown = false;
    }
  }

  ngOnInit() {
    // Set initial values for pagination
    this.currentPage = 1;
    this.perPage = 10;
    this.isCompleted = 0; // Ensure "All" is selected by default
    this.previousCompletionStatus = 0;

    console.log('SendlanePostscriptComponent ngOnInit - initialData:', this.initialData);
    console.log('SendlanePostscriptComponent ngOnInit - autoLoad:', this.autoLoad);
    
    if (this.initialData) {
      console.log('Processing initialData in ngOnInit...');
      this.handleResponse(this.initialData);
    } else if (this.autoLoad) {
      console.log('Auto-loading data in ngOnInit...');
      this.loadData();
    } else {
      console.log('No initialData and autoLoad is false, not loading data');
    }
  }

  ngAfterViewInit() {
    this.setupInfiniteScroll();
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('SendlanePostscriptComponent ngOnChanges called with changes:', changes);

    // Check if fromDate or toDate inputs have changed
    const fromDateChanged = changes['fromDate'] && !changes['fromDate'].firstChange;
    const toDateChanged = changes['toDate'] && !changes['toDate'].firstChange;

    // Restore initial values for isFailed and isCompleted if they were changed
    if (changes['isFailed'] && !changes['isFailed'].firstChange) {
      this.isFailed = this.initialIsFailed;
    }
    if (changes['isCompleted'] && !changes['isCompleted'].firstChange) {
      this.isCompleted = this.initialIsCompleted;
    }

    if (changes['initialData'] && changes['initialData'].currentValue) {
      console.log('Processing initialData change in ngOnChanges:', changes['initialData'].currentValue);
      this.handleResponse(changes['initialData'].currentValue);
    }
    // Handle date range changes
    else if (fromDateChanged || toDateChanged) {
      console.log('Date range changed, reloading data...', { fromDate: this.fromDate, toDate: this.toDate });
      // Update internal date filter when external dates change
      if (this.fromDate && this.toDate) {
        this.setFilterFromDates(this.fromDate, this.toDate);
      } else {
        this.selectedDateFilter = 'default';
      }
      this.loadData();
    }
    else if (this.autoLoad && !changes['autoLoad'] && !changes['initialData']) {
      console.log('Auto-loading data due to other changes...');
      this.loadData();
    }
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  private setupInfiniteScroll() {
    if (!this.timelineContainer) return;

    const scrollEvent$ = fromEvent(this.timelineContainer.nativeElement, 'scroll')
      .pipe(debounceTime(150));

    this.scrollSubscription = scrollEvent$.subscribe(() => {
      this.onScroll();
    });
  }

  private onScroll() {
    if (!this.timelineContainer || this.isLoadingMore || !this.hasMoreData) return;

    const container = this.timelineContainer.nativeElement;
    const threshold = 200;
    
    const scrollPosition = container.scrollTop;
    const totalHeight = container.scrollHeight;
    const visibleHeight = container.clientHeight;
    
    const distanceFromBottom = totalHeight - (scrollPosition + visibleHeight);

    if (distanceFromBottom < threshold) {
      this.loadMore();
    }
  }

  private buildPayload(page: number = this.currentPage): any {
    const payload: any = {
      per_page: this.perPage,
      page: page
    };

    // Add registrationId if it exists
    if (this.registrationId) {
      payload.registrationId = this.registrationId;
    }

    // Add completion status filters
    if (this.isCompleted === 1) {
      payload.isCompleted = 1;
    } else if (this.isCompleted === 2) {
      payload.isFailed = 1;
    }

    // Prioritize external date inputs from parent component
    if (this.fromDate && this.toDate) {
      payload.fromDate = this.formatDateForApi(this.fromDate);
      payload.toDate = this.formatDateForApi(this.toDate);
    }
    // Fall back to internal date filter selection if no external dates
    else if (this.selectedDateFilter !== 'default' && this.fromDate && this.toDate) {
      payload.fromDate = this.formatDateForApi(this.fromDate);
      payload.toDate = this.formatDateForApi(this.toDate);
    }

    return payload;
  }

  async loadData() {
    try {
      // Reset pagination when loading new data
      this.currentPage = 1;
      this.data = [];
      this.hasMoreData = true;

      const payload = this.buildPayload(1);
      const response = await this.commonService.getSendlanePostscript(payload);
      this.handleResponse(response);
      this.dataLoaded.emit(response);
    } catch (err) {
      console.error('Error loading data:', err);
      this.error.emit(err);
    }
  }

  private async loadMore() {
    if (this.isLoadingMore || !this.hasMoreData) return;
    
    this.isLoadingMore = true;
    
    try {
      const nextPage = this.currentPage + 1;
      const payload = this.buildPayload(nextPage);
      
      const response = await this.commonService.getSendlanePostscript(payload);
      this.handleResponse(response);
    } catch (err) {
      console.error('Error loading more data:', err);
      this.error.emit(err);
      this.hasMoreData = false;
    } finally {
      this.isLoadingMore = false;
    }
  }

  // Helper method to determine the filter based on provided dates
  private setFilterFromDates(fromDate?: Date | string, toDate?: Date | string) {
    if (!fromDate || !toDate) return;

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if it's today
    if (from.getTime() === today.getTime() && to.getTime() === today.getTime()) {
      this.selectedDateFilter = 'today';
      return;
    }

    // Check if it's yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (from.getTime() === yesterday.getTime() && to.getTime() === yesterday.getTime()) {
      this.selectedDateFilter = 'yesterday';
      return;
    }

    // Calculate the difference in days
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    // Set the appropriate filter based on the number of days
    if (diffDays === 7) this.selectedDateFilter = '7days';
    else if (diffDays === 15) this.selectedDateFilter = '15days';
    else if (diffDays === 30) this.selectedDateFilter = '30days';
    else if (diffDays === 90) this.selectedDateFilter = '90days';
    else {
      this.selectedDateFilter = 'custom';
      this.customDateRange = [new Date(fromDate), new Date(toDate)];
      this.tempCustomDateRange = [...this.customDateRange];
    }
  }

  // Date filter methods
  toggleFilterDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showFilterDropdown = !this.showFilterDropdown;
    if (this.showFilterDropdown) {
      this.showCustomDatePicker = false;
    }
  }

  applyDateFilter(filterId: string) {
    this.selectedDateFilter = filterId;
    this.showFilterDropdown = false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (filterId) {
      case 'today':
        this.fromDate = this.formatDate(today);
        this.toDate = this.formatDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        this.fromDate = this.formatDate(yesterday);
        this.toDate = this.formatDate(yesterday);
        break;
      case '7days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        this.fromDate = this.formatDate(sevenDaysAgo);
        this.toDate = this.formatDate(today);
        break;
      case '15days':
        const fifteenDaysAgo = new Date(today);
        fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
        this.fromDate = this.formatDate(fifteenDaysAgo);
        this.toDate = this.formatDate(today);
        break;
      case '30days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        this.fromDate = this.formatDate(thirtyDaysAgo);
        this.toDate = this.formatDate(today);
        break;
      case '90days':
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        this.fromDate = this.formatDate(ninetyDaysAgo);
        this.toDate = this.formatDate(today);
        break;
      default:
        this.fromDate = undefined;
        this.toDate = undefined;
        break;
    }

    // Refresh data with new date range
    this.loadData();
  }

  toggleCustomDatePicker() {
    this.showCustomDatePicker = !this.showCustomDatePicker;
    this.showFilterDropdown = false;
    if (this.showCustomDatePicker) {
      // Initialize with current date range if exists
      if (this.fromDate && this.toDate) {
        this.customDateRange = [new Date(this.fromDate), new Date(this.toDate)];
      }
    }
  }

  onCustomDateSelect(event: any) {
    this.tempCustomDateRange = [...this.customDateRange];
  }

  cancelCustomDateRange() {
    this.showCustomDatePicker = false;
    this.customDateRange = [...this.tempCustomDateRange];
  }

  applyCustomDateRange() {
    if (this.tempCustomDateRange?.length === 2) {
      this.selectedDateFilter = 'custom';
      this.fromDate = this.formatDate(this.tempCustomDateRange[0]);
      this.toDate = this.formatDate(this.tempCustomDateRange[1]);
      this.showCustomDatePicker = false;
      
      // Refresh data with new date range
      this.loadData();
    }
  }

  resetDateFilter(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedDateFilter = 'default';
    this.fromDate = undefined;
    this.toDate = undefined;
    this.customDateRange = [];
    this.tempCustomDateRange = [];
    
    // Refresh data without date range
    this.loadData();
  }

  getSelectedFilterLabel(): string {
    switch (this.selectedDateFilter) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case '7days':
        return 'Last 7 Days';
      case '15days':
        return 'Last 15 Days';
      case '30days':
        return 'Last 30 Days';
      case '90days':
        return 'Last 90 Days';
      case 'custom':
        if (this.fromDate && this.toDate) {
          const from = formatDate(this.fromDate, 'MMM d, y', 'en-US');
          const to = formatDate(this.toDate, 'MMM d, y', 'en-US');
          return `${from} - ${to}`;
        }
        return 'Custom Range';
      default:
        return 'Last 7 Days'; // Default label
    }
  }

  private handleResponse(response: any) {
    console.log('SendlanePostscriptComponent handleResponse called with:', response);
    
    if (response?.data) {
      // Check if data is nested in a data property
      let responseData;
      if (Array.isArray(response.data)) {
        // If response.data is already an array, use it directly
        responseData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // If data is nested like response.data.data, extract it
        responseData = response.data.data;
      } else {
        // Fallback to treating response.data as the array
        responseData = response.data;
      }
      
      console.log('Extracted data:', responseData);
      
      // Ensure responseData is an array
      if (!Array.isArray(responseData)) {
        console.warn('Response data is not an array:', responseData);
        responseData = [];
      }
      
      // If it's first page, replace data, otherwise append
      if (this.currentPage === 1) {
        this.data = responseData;
      } else {
        this.data = [...this.data, ...responseData];
      }
      
      // Handle pagination info
      if (response.data.total !== undefined) {
        this.totalRecords = response.data.total;
        this.currentPage = response.data.current_page || this.currentPage;
        this.hasMoreData = responseData.length > 0 && response.data.current_page < response.data.last_page;
      } else {
        this.totalRecords = response.total || responseData.length;
        this.currentPage = response.current_page || this.currentPage;
        this.hasMoreData = responseData.length > 0 && this.currentPage < Math.ceil(this.totalRecords / this.perPage);
      }
      
      console.log('Final component state:', {
        dataLength: this.data.length,
        totalRecords: this.totalRecords,
        currentPage: this.currentPage,
        hasMoreData: this.hasMoreData
      });
    } else {
      console.warn('No data found in response:', response);
      this.data = [];
    }
    
    // Manually trigger change detection to ensure UI updates
    this.changeDetectorRef.detectChanges();
  }

  private formatDateForApi(date: Date | string | undefined): string | undefined {
    if (!date) return undefined;
    if (date instanceof Date) {
      return formatDate(date, 'yyyy-MM-dd', 'en-US');
    }
    return date;
  }

  private formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  refresh() {
    this.loadData();
  }

  viewDetails(item: any) {
    const details = {
      payload: this.formatJson(item.Payload),
      response: this.formatJson(item.Response)
    };

    const modalRef = this.modalService.open(SendlaneDetailsModalComponent, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.details = details;
    modalRef.componentInstance.item = item;
  }

  async retryItem(item: any) {
    try {
      await this.commonService.getSendlanePostscript({
        retryId: item.id,
        per_page: this.perPage
      });
      this.commonService.showToast('success', 'Success', 'Retry initiated successfully');
      this.refresh();
    } catch (error) {
      this.commonService.showToast('error', 'Error', 'Failed to retry the item');
    }
  }

  private formatJson(jsonString: string): string {
    try {
      const obj = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return jsonString;
    }
  }

  // Helper method to get severity for p-tag
  getSeverity(item: any): string {
    if (item.IsFailed) return 'danger';
    if (item.IsCompleted) return 'success';
    return 'warning';
  }

  // Helper method to get status text
  getStatusText(item: any): string {
    if (item.IsFailed) return 'Failed';
    if (item.IsCompleted) return 'Completed';
    return 'Pending';
  }
} 